import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SeedCalculator } from './components/SeedCalculator';
import { VarietyMapper } from './components/VarietyMapper';
import { HomePage } from './components/HomePage';
import { ContactPage } from './components/ContactPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { CountryPicker } from './components/CountryPicker';
import { useAuth } from './hooks/useAuth';
import { UserRole } from '@spi/shared';

// Marketplace
import { MarketplaceLayout } from './components/marketplace/shared/MarketplaceLayout';
import { RoleGuard } from './components/marketplace/shared/RoleGuard';
import { MarketplaceLanding } from './components/marketplace/MarketplaceLanding';
import { LoginPage } from './components/marketplace/auth/LoginPage';
import { ApplyPage } from './components/marketplace/auth/ApplyPage';
import { ForgotPasswordPage } from './components/marketplace/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './components/marketplace/auth/ResetPasswordPage';
import { VerifyEmailPage } from './components/marketplace/auth/VerifyEmailPage';
import { DashboardPage } from './components/marketplace/dashboard/DashboardPage';
import { ProjectsListPage } from './components/marketplace/projects/ProjectsListPage';
import { ProjectDetailPage } from './components/marketplace/projects/ProjectDetailPage';
import { CreateProjectPage } from './components/marketplace/projects/CreateProjectPage';
import { EditProjectPage } from './components/marketplace/projects/EditProjectPage';
import { TermsPage } from './components/legal/TermsPage';
import { PrivacyPage } from './components/legal/PrivacyPage';
import { DonationsPage } from './components/marketplace/donations/DonationsPage';
import { DonationDetailPage } from './components/marketplace/donations/DonationDetailPage';
import { AdminPage } from './components/marketplace/admin/AdminPage';
import { ProfilePage } from './components/marketplace/profile/ProfilePage';
import { IrsFormsPage } from './components/marketplace/irs/IrsFormsPage';
import { MapPage } from './components/marketplace/map/MapPage';
import { OrganizationDetailPage } from './components/marketplace/organizations/OrganizationDetailPage';
import { UserPage } from './components/marketplace/users/UserPage';

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/marketplace/login" replace />;
  }

  return <>{children}</>;
}

function Navigation() {
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === UserRole.ADMIN;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-spi-parchment">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-xl bg-[#1A3F1E] flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
              <svg viewBox="0 0 32 32" className="w-7 h-7">
                <path d="M9 25 C9 25 10.5 11 21 6 C21 6 23 18 13 25 Z" fill="#4CAF50"/>
                <path d="M10.5 23.5 Q14 16 20.5 7.5" fill="none" stroke="#1A3F1E" strokeWidth="1.2" strokeLinecap="round"/>
                <circle cx="16" cy="25.5" r="3.2" fill="#F5A623"/>
                <path d="M17.8 23 C18 20.5 16.5 19 15 18.5" fill="none" stroke="#4CAF50" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="text-left">
              <h1 className="text-lg font-display text-spi-green-dark leading-tight">
                {t('nav.brand')}
              </h1>
              <p className="text-xs text-spi-slate">{t('nav.brandSubtitle')}</p>
            </div>
          </Link>

          <div className="flex items-center gap-4">
            <nav className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/'
                    ? 'text-spi-green'
                    : 'text-spi-slate hover:text-spi-green'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/seed-calculator"
                className={`text-sm font-medium transition-colors ${
                  location.pathname === '/seed-calculator'
                    ? 'text-spi-green'
                    : 'text-spi-slate hover:text-spi-green'
                }`}
              >
                {t('nav.seedCalculator')}
              </Link>
              {isAdmin && (
                <Link
                  to="/variety-mapper"
                  className={`text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                    location.pathname === '/variety-mapper'
                      ? 'text-spi-green'
                      : 'text-spi-slate hover:text-spi-green'
                  }`}
                >
                  {t('nav.varietyMapper')}
                  <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-semibold leading-none">Alpha</span>
                </Link>
              )}
              <Link
                to="/marketplace"
                className={`text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/marketplace')
                    ? 'text-spi-green'
                    : 'text-spi-slate hover:text-spi-green'
                }`}
              >
                Second Chance Seed Network
              </Link>
            </nav>

            <CountryPicker />

            <div className="md:hidden">
              {location.pathname === '/' ? (
                <Link to="/seed-calculator" className="btn btn-primary text-sm py-2">
                  {t('nav.calculator')}
                </Link>
              ) : (
                <Link to="/" className="btn btn-primary text-sm py-2">
                  {t('nav.home')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useTranslation();

  return (
    <footer className="bg-spi-green-dark text-white py-8 mt-auto">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
              <svg viewBox="0 0 32 32" className="w-5 h-5">
                <path d="M9 25 C9 25 10.5 11 21 6 C21 6 23 18 13 25 Z" fill="#4CAF50"/>
                <circle cx="16" cy="25.5" r="3.2" fill="#F5A623"/>
              </svg>
            </div>
            <a href="https://baycrewtech.com" target="_blank" rel="noopener noreferrer" className="text-sm opacity-80 hover:opacity-100 hover:underline">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </a>
          </div>
          <div className="text-sm opacity-80">
            {t('footer.tagline')}
          </div>
        </div>
      </div>
    </footer>
  );
}

const ROUTE_TITLE_KEYS: Record<string, string> = {
  '/': 'pageTitles.home',
  '/seed-calculator': 'pageTitles.seedCalculator',
  '/variety-mapper': 'pageTitles.varietyMapper',
};

function MainLayout() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const key = ROUTE_TITLE_KEYS[pathname] || ROUTE_TITLE_KEYS['/'];
    if (key) document.title = t(key);
  }, [pathname, t]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/seed-calculator"
              element={
                <div className="container mx-auto px-4 py-8">
                  <SeedCalculator />
                </div>
              }
            />
            <Route
              path="/variety-mapper"
              element={
                <div className="container mx-auto px-4 py-8">
                  <VarietyMapper />
                </div>
              }
            />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Marketplace routes - own layout */}
          <Route path="/marketplace" element={<MarketplaceLayout />}>
            <Route index element={<MarketplaceLanding />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="apply" element={<ApplyPage />} />
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="reset-password" element={<ResetPasswordPage />} />
            <Route path="verify-email" element={<VerifyEmailPage />} />
            <Route path="projects" element={<ProjectsListPage />} />
            <Route path="projects/:id" element={<ProjectDetailPage />} />
            <Route path="projects/new" element={
              <AuthGuard><RoleGuard roles={[UserRole.RECIPIENT]}><CreateProjectPage /></RoleGuard></AuthGuard>
            } />
            <Route path="projects/:id/edit" element={
              <AuthGuard><RoleGuard roles={[UserRole.RECIPIENT]}><EditProjectPage /></RoleGuard></AuthGuard>
            } />
            <Route path="terms" element={<TermsPage />} />
            <Route path="privacy" element={<PrivacyPage />} />
            <Route path="dashboard" element={
              <AuthGuard><DashboardPage /></AuthGuard>
            } />
            <Route path="donations" element={
              <AuthGuard><DonationsPage /></AuthGuard>
            } />
            <Route path="donations/:id" element={
              <AuthGuard><DonationDetailPage /></AuthGuard>
            } />
            <Route path="admin" element={
              <AuthGuard><RoleGuard roles={[UserRole.ADMIN]}><AdminPage /></RoleGuard></AuthGuard>
            } />
            <Route path="profile" element={
              <AuthGuard><ProfilePage /></AuthGuard>
            } />
            <Route path="irs-forms" element={
              <AuthGuard><RoleGuard roles={[UserRole.DONOR, UserRole.ADMIN]}><IrsFormsPage /></RoleGuard></AuthGuard>
            } />
            <Route path="map" element={
              <AuthGuard><MapPage /></AuthGuard>
            } />
            <Route path="organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="users/:id" element={<UserPage />} />
          </Route>

          {/* Main site routes */}
          <Route path="/*" element={<MainLayout />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
